/**
 * Activity / Servicing panel (business pages slice 3).
 * Organization Interactions tab (organizationId set) and the global Interactions page (no organization).
 * List + create/edit modal + a detail view showing Call Plan and Results side by side.
 * Admins also get "Manage lists" for the Subject / Activity Type / Goal picklists.
 */

import React, { useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { Interaction } from "../../types/interaction.types";
import type { UUID } from "../../types/common.types";
import { getInteraction } from "../../services/interactions.service";
import {
  getLookupValues,
  isRewardInteraction,
} from "../../services/interactionServicing.service";
import { formatDate } from "../../utils/formatters";
import { normalizeRole } from "../../utils/roleUtils";
import { useAuth } from "../../contexts/AuthContext";
import Button from "../common/Button";
import Modal from "../common/Modal";
import InteractionForm, { LOOKUP_QUERY_KEY } from "./InteractionForm";
import InteractionsList from "./InteractionsList";
import ServicingListsManager from "./ServicingListsManager";

interface InteractionsTabProps {
  organizationId?: UUID;
  locationId?: UUID;
  /** open this interaction's detail view on mount (deep link /interactions?interaction=<id>) */
  initialInteractionId?: UUID | null;
  title?: string;
  description?: string;
}

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  <div>
    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
      {label}
    </div>
    <div className="mt-1 text-sm text-gray-900 whitespace-pre-wrap">
      {children}
    </div>
  </div>
);

const InteractionsTab: React.FC<InteractionsTabProps> = ({
  organizationId,
  locationId,
  initialInteractionId,
  title = "Activity / Servicing",
  description = "Call plans, results and other servicing activity for this account, newest first.",
}) => {
  const { user } = useAuth();
  const isAdmin = ["superadmin", "admin"].includes(normalizeRole(user?.role));
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isListsOpen, setIsListsOpen] = useState(false);
  const [editing, setEditing] = useState<Interaction | null>(null);
  const [viewing, setViewing] = useState<Interaction | null>(null);

  const { data: deepLinked } = useQuery({
    queryKey: ["interaction", initialInteractionId],
    queryFn: () => getInteraction(initialInteractionId as UUID),
    enabled: !!initialInteractionId,
  });
  useEffect(() => {
    if (deepLinked) {
      setViewing(deepLinked);
    }
  }, [deepLinked]);

  const lookups = useQuery({
    queryKey: [LOOKUP_QUERY_KEY, "all-for-view"],
    queryFn: async () => {
      const [s, a, g] = await Promise.all([
        getLookupValues("subjects"),
        getLookupValues("activity_types"),
        getLookupValues("goal_sessions"),
      ]);
      return new Map([...s, ...a, ...g].map((v) => [v.id, v.label]));
    },
    enabled: !!viewing,
  });
  const label = (id?: string | null) =>
    (id && lookups.data?.get(id)) || (id ? "…" : "—");

  return (
    <div className="space-y-6" data-testid="servicing-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-600">{description}</p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <Button
              variant="secondary"
              onClick={() => setIsListsOpen(true)}
              data-testid="servicing-manage-lists"
            >
              Manage lists
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => setIsCreateModalOpen(true)}
            data-testid="servicing-add"
          >
            + Add Activity
          </Button>
        </div>
      </div>

      <InteractionsList
        organizationId={organizationId}
        locationId={locationId}
        onEdit={setEditing}
        onView={setViewing}
        onAdd={() => setIsCreateModalOpen(true)}
      />

      <Modal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        title="Add Activity"
        size="lg"
      >
        {isCreateModalOpen && (
          <InteractionForm
            organizationId={organizationId}
            locationId={locationId}
            onSuccess={() => setIsCreateModalOpen(false)}
            onCancel={() => setIsCreateModalOpen(false)}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!editing}
        onClose={() => setEditing(null)}
        title="Edit Activity"
        size="lg"
      >
        {editing && (
          <InteractionForm
            key={editing.id}
            interaction={editing}
            organizationId={organizationId}
            onSuccess={() => setEditing(null)}
            onCancel={() => setEditing(null)}
          />
        )}
      </Modal>

      <Modal
        isOpen={!!viewing}
        onClose={() => setViewing(null)}
        title="Activity Details"
        size="lg"
      >
        {viewing && (
          <div className="space-y-5" data-testid="servicing-view">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {viewing.title}
              </h3>
              <p className="text-sm text-gray-500">
                {formatDate(viewing.interaction_date)}
                {viewing.interaction_time
                  ? ` at ${String(viewing.interaction_time).slice(0, 5)}`
                  : ""}
                {isRewardInteraction(viewing) ? " · Event reward record" : ""}
                {viewing.is_internal ? " · Internal" : ""}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label="Subject">{label(viewing.subject_id)}</Field>
              <Field label="Activity Type">
                {label(viewing.activity_type_id)}
              </Field>
              <Field label="Next Task Due">
                {viewing.next_follow_up_date
                  ? formatDate(viewing.next_follow_up_date)
                  : "—"}
              </Field>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-md bg-gray-50 p-3">
                <Field label="Call Plan">{viewing.call_plan || "—"}</Field>
              </div>
              <div className="rounded-md bg-gray-50 p-3">
                <Field label="Results">{viewing.results || "—"}</Field>
              </div>
            </div>
            {viewing.description && (
              <Field label="Notes">{viewing.description}</Field>
            )}
            {viewing.goal_session_id && (
              <Field label="Related Goal / Session">
                {label(viewing.goal_session_id)}
              </Field>
            )}
            <div className="flex justify-end space-x-3 pt-4 border-t">
              <Button variant="secondary" onClick={() => setViewing(null)}>
                Close
              </Button>
              <Button
                variant="primary"
                onClick={() => {
                  const v = viewing;
                  setViewing(null);
                  setEditing(v);
                }}
              >
                Edit
              </Button>
            </div>
          </div>
        )}
      </Modal>

      {isAdmin && (
        <Modal
          isOpen={isListsOpen}
          onClose={() => setIsListsOpen(false)}
          title="Servicing picklists"
          size="lg"
        >
          {isListsOpen && <ServicingListsManager />}
        </Modal>
      )}
    </div>
  );
};

export default InteractionsTab;
